// ── Design decisions ──
// Riel horizontal de bultos: cada bulto es una tarjeta angosta con etiqueta,
// peso y dimensiones editables, más dos datos derivados (cuántos paquetes lo
// usan y su subtotal FOB) para que el viajero vea de un vistazo cómo se
// reparte la carga. El riel hace scroll horizontal en vez de apilarse porque
// en un viaje normal hay 2–6 bultos y la lista de paquetes necesita el alto.
// Quitar es de dos toques (armar → confirmar) como en AnexoItemRow; el
// contenedor se encarga de dejar los paquetes con bulto_id null.
// BultoSelect vive acá para compartir la forma de nombrar bultos; cuando no
// hay bulto muestra el chip ámbar "Sin bulto" (advertencia, no bloqueo).

import { useEffect, useState } from 'react';
import { Package, Plus, Trash2 } from 'lucide-react';
import { fmtUsd, paqueteFob } from './labels';
import { SinBultoChip } from './chips';

const inputBase =
  'rounded-md border border-slate-200 bg-[#f8fafc] px-2 py-1.5 text-xs text-slate-900 ' +
  'outline-none focus:border-[var(--brand-teal)] transition-colors disabled:opacity-50';

export const nombreBulto = (b, i) => b?.label || `Bulto ${(i ?? 0) + 1}`;

export const BultoCard = ({ bulto, index, paquetes = [], onChange, onQuitar, disabled = false }) => {
  const [confirmar, setConfirmar] = useState(false);
  useEffect(() => {
    if (!confirmar) return undefined;
    const t = setTimeout(() => setConfirmar(false), 4000);
    return () => clearTimeout(t);
  }, [confirmar]);

  const delBulto = paquetes.filter((p) => p.bulto_id === bulto.id);
  const subtotal = delBulto.reduce((s, p) => s + paqueteFob(p), 0);
  const n = delBulto.length;

  return (
    <article
      aria-label={`Bulto ${nombreBulto(bulto, index)}`}
      className="flex w-52 shrink-0 flex-col gap-1.5 rounded-lg border border-slate-200 bg-white p-2.5"
    >
      <div className="flex items-center gap-1.5">
        <Package size={13} className="shrink-0 text-slate-400" aria-hidden="true" />
        <input
          type="text"
          value={bulto.label || ''}
          placeholder={`Bulto ${(index ?? 0) + 1}`}
          aria-label="Etiqueta del bulto"
          disabled={disabled}
          onChange={(e) => onChange && onChange(bulto.id, { label: e.target.value })}
          className={`${inputBase} min-w-0 flex-1 font-medium`}
        />
        {!disabled &&
          (confirmar ? (
            <button
              type="button"
              onClick={() => onQuitar && onQuitar(bulto.id)}
              aria-label="Confirmar eliminación del bulto"
              className="rounded-md border border-red-300 bg-red-50 px-1.5 py-1 text-[10px] font-semibold text-red-600"
            >
              Confirmar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmar(true)}
              aria-label="Eliminar bulto"
              title="Eliminar bulto"
              className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
            >
              <Trash2 size={13} aria-hidden="true" />
            </button>
          ))}
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <label className="flex flex-col gap-0.5 text-[10px] font-semibold uppercase text-slate-400">
          Peso (lb)
          <input
            type="number"
            min={0}
            step={0.1}
            value={bulto.peso_lb ?? ''}
            aria-label="Peso (lb)"
            disabled={disabled}
            onChange={(e) => onChange && onChange(bulto.id, { peso_lb: e.target.value })}
            className={`${inputBase} w-full text-right font-mono normal-case`}
          />
        </label>
        <label className="flex flex-col gap-0.5 text-[10px] font-semibold uppercase text-slate-400">
          Dimensiones
          <input
            type="text"
            value={bulto.dimensiones || ''}
            placeholder="60×40×40 cm"
            aria-label="Dimensiones"
            disabled={disabled}
            onChange={(e) => onChange && onChange(bulto.id, { dimensiones: e.target.value })}
            className={`${inputBase} w-full normal-case`}
          />
        </label>
      </div>
      <div className="flex items-center justify-between text-[11px] text-slate-500">
        <span>{n === 1 ? '1 paquete' : `${n} paquetes`}</span>
        <span className="font-mono font-semibold text-slate-900">Subtotal FOB ${fmtUsd(subtotal)}</span>
      </div>
    </article>
  );
};

// `ambiance` (sólo el ExtractionDrawer) cambia la piel del <select> por los
// tokens de ambiance.css. El chip "Sin bulto" queda tal cual en las dos
// variantes: su ámbar es una excepción explícita al rediseño.
export const BultoSelect = ({ bultos = [], value, onChange, disabled = false, ambiance = false }) => (
  <span className="inline-flex items-center gap-1.5">
    <select
      aria-label="Bulto del paquete"
      value={value ?? ''}
      disabled={disabled}
      onChange={(e) => onChange && onChange(e.target.value === '' ? null : e.target.value)}
      className={ambiance ? 'amb-in amb-sel w-36' : `${inputBase} w-36`}
    >
      <option value="">Sin bulto</option>
      {bultos.map((b, i) => (
        <option key={b.id} value={b.id}>
          {nombreBulto(b, i)}
        </option>
      ))}
    </select>
    {!value && <SinBultoChip />}
  </span>
);

const BultoRail = ({ bultos = [], paquetes = [], onAgregar, onChange, onQuitar, disabled = false }) => (
  <section aria-label="Bultos del viaje" className="flex flex-col gap-2">
    {bultos.length === 0 ? (
      <p className="rounded-lg border border-dashed border-slate-300 bg-[#f8fafc] px-3 py-4 text-center text-xs text-slate-500">
        Empezá creando tu primer bulto para este viaje.
      </p>
    ) : (
      <div className="flex gap-2 overflow-x-auto pb-1">
        {bultos.map((b, i) => (
          <BultoCard
            key={b.id}
            bulto={b}
            index={i}
            paquetes={paquetes}
            onChange={onChange}
            onQuitar={onQuitar}
            disabled={disabled}
          />
        ))}
      </div>
    )}
    {!disabled && (
      <button
        type="button"
        onClick={() => onAgregar && onAgregar()}
        className="inline-flex min-h-[44px] w-fit items-center gap-1.5 rounded-md bg-slate-900 px-3 text-xs font-semibold text-white hover:bg-slate-800"
      >
        <Plus size={14} aria-hidden="true" /> Agregar bulto
      </button>
    )}
  </section>
);

export default BultoRail;
